<template>
  <div class="sec-scorer">
    <div class="sec-scorer__progress"><i :style="{ width: progress + '%' }"></i></div>

    <div ref="body" class="sec-scorer__body" aria-live="polite">
      <score-question
        v-if="step < questions.length"
        :key="step"
        :question="questions[step]"
        :index="step"
        :total="questions.length"
        @answer="answer"
      />
      <score-gate
        v-else-if="!gated"
        v-model:email="email"
        v-model:band="band"
        :submitting="submitting"
        @submit="submitGate"
      />
      <score-result v-else :answers="answers" />
    </div>

    <div class="m500 sec-scorer__foot">
      <span>{{ foot }}</span>
      <button v-if="step > 0" type="button" class="m600 sec-scorer__restart" @click="restart">
        {{ copy.restart }}
      </button>
    </div>
  </div>
</template>

<script setup>
// The ten-question self score. Scoring runs entirely in the browser. At the gate, the email
// (and only the email) is subscribed to the newsletter before the result shows.
// Not animated on purpose, so answering never waits on motion.
import { ref, computed, watch, nextTick } from 'vue';
import ScoreQuestion from './ScoreQuestion.vue';
import ScoreGate from './ScoreGate.vue';
import ScoreResult from './ScoreResult.vue';
import { QUESTIONS, SCORER_COPY } from '@/content/security/score';
import securityNewsletterService from '@/services/API/securityNewsletterService';

const questions = QUESTIONS;
const copy = SCORER_COPY;

const answers = ref([]);
const step = ref(0);
const gated = ref(false);
const email = ref('');
const band = ref(copy.gate.defaultBand);
const submitting = ref(false);
const body = ref(null);

// Each answer replaces the button that was clicked, so move keyboard and screen-reader
// focus to the new step's heading ([data-scorer-focus]) instead of losing it to <body>.
watch([step, gated], async () => {
  await nextTick();
  const target = body.value && body.value.querySelector('[data-scorer-focus]');
  if (target) target.focus({ preventScroll: true });
});

const progress = computed(() =>
  step.value < questions.length ? (step.value / questions.length) * 100 : 100
);

const foot = computed(() => {
  if (step.value < questions.length) return step.value === 0 ? copy.footIntro : copy.footQuestion;
  return gated.value ? copy.footResult : copy.footGate;
});

function answer(value) {
  answers.value[step.value] = value;
  step.value += 1;
}

// Subscribe the email (only the email is sent), then show the result. A failed request
// is logged but never blocks the visitor from their score.
async function submitGate() {
  submitting.value = true;
  try {
    await securityNewsletterService.subscribeEmail(email.value.trim());
  } catch (error) {
    console.error('Newsletter subscription failed:', error);
  } finally {
    submitting.value = false;
    gated.value = true;
  }
}

function restart() {
  answers.value = [];
  step.value = 0;
  gated.value = false;
}
</script>

<style scoped>
.sec-scorer {
  background: var(--sec-surface);
  border: 1px solid var(--sec-line-strong);
  border-radius: var(--sec-radius-card);
  overflow: hidden;
}

.sec-scorer__progress {
  height: 3px;
  background: var(--sec-track);
}

.sec-scorer__progress i {
  display: block;
  height: 100%;
  background: var(--sec-accent);
  transition: width 0.25s ease;
}

.sec-scorer__body {
  display: flex;
  flex-direction: column;
  gap: var(--sec-s3);
  min-height: 238px;
  padding: var(--sec-s3);
}

.sec-scorer__foot {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: var(--sec-s2);
  padding: var(--sec-s2) var(--sec-s3);
  border-top: 1px solid var(--sec-line);
  background: var(--sec-surface-2);
  font-size: 0.88rem;
  color: var(--sec-ink-2);
}

.sec-scorer__restart {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: var(--sec-accent-text);
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

@media only screen and (min-width: 700px) {
  .sec-scorer__body {
    padding: var(--sec-s4);
  }
  .sec-scorer__foot {
    padding-inline: var(--sec-s4);
  }
}

@media (prefers-reduced-motion: reduce) {
  .sec-scorer__progress i {
    transition: none;
  }
}
</style>
